import { type CSSProperties, type FormEvent, useState } from 'react'
import BrandLogo from '../components/BrandLogo'
import { Button, ButtonLink, Card, CardContent, Callout, TextField, IconButton } from '../components/ui'

/**
 * La pagina sta fuori dalla shell: gestisce da sola le aree sicure e i segmenti dello schermo. Con due segmenti
 * affiancati il modulo sta nel segmento 1 e non attraversa mai la cerniera; senza segmenti --md-seg1-* valgono
 * l'intero viewport. Con segmenti sovrapposti (tabletop) sta nel segmento superiore.
 */
const PAGE_STYLE: CSSProperties = {
  minHeight: 'min(100svh, var(--md-seg0-height))',
  paddingTop: 'var(--md-safe-top)',
  paddingBottom: 'var(--md-safe-bottom)',
  paddingLeft: 'max(var(--md-safe-left), var(--md-seg1-left))',
  paddingRight: 'max(var(--md-safe-right), calc(100vw - var(--md-seg1-left) - var(--md-seg1-width)))',
}
const THEME_TOGGLE_STYLE: CSSProperties = {
  top: 'max(16px, var(--md-safe-top))',
  insetInlineEnd: 'max(16px, var(--md-safe-right), calc(100vw - var(--md-seg1-left) - var(--md-seg1-width) + 16px))',
}

export function ResetPasswordPage() {
  const params = new URLSearchParams(window.location.search)
  const token = params.get('token') ?? ''
  const email = params.get('email') ?? ''

  const [theme, setTheme] = useState(
    () => document.documentElement.getAttribute('data-theme') || 'dark',
  )
  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [loading, setLoading] = useState(false)

  function toggleTheme() {
    const next = theme === 'dark' ? 'light' : 'dark'
    document.documentElement.setAttribute('data-theme', next)
    try { localStorage.setItem('ecrm_theme_mode', next) } catch { /* ignore */ }
    setTheme(next)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (newPassword !== confirm) {
      setError('Le password non coincidono.')
      return
    }
    setError(null)
    setLoading(true)
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token, newPassword }),
      })
      if (!res.ok) {
        const body = (await res.json()) as { detail?: string }
        setError(body.detail ?? 'Errore durante il reset. Il link potrebbe essere scaduto.')
      } else {
        setDone(true)
      }
    } catch {
      setError('Errore di rete. Riprova.')
    } finally {
      setLoading(false)
    }
  }

  const pageWrapper = (children: React.ReactNode) => (
    <div style={PAGE_STYLE} className="bg-body d-flex align-items-center justify-content-center position-relative">
      {/* Theme toggle */}
      <IconButton
        icon={theme === 'dark' ? 'light_mode' : 'dark_mode'}
        variant="tonal"
        aria-label="Attiva/disattiva tema chiaro/scuro"
        onClick={toggleTheme}
        style={THEME_TOGGLE_STYLE}
        className="position-fixed z-3"
      />

      {/* Card wrapper */}
      <div className="w-100 p-3 md-u-maxw-432">
        <Card variant="outlined" padding="md">
          <CardContent>

            {/* Brand */}
            <div className="text-center mb-4">
              <BrandLogo height={64} />
              <p className="text-muted md-typescale-body-small mt-1 mb-0">
                Management Suite
              </p>
            </div>

            {children}
          </CardContent>
        </Card>
      </div>
    </div>
  )

  if (!token || !email) {
    return pageWrapper(
      <>
        <Callout tone="error" role="alert" className="mb-3">
          Link non valido o scaduto.
        </Callout>
        <div className="text-center">
          <ButtonLink to="/forgot-password" variant="text" size="sm">
            Richiedi un nuovo link
          </ButtonLink>
        </div>
      </>,
    )
  }

  if (done) {
    return pageWrapper(
      <>
        <Callout tone="success" role="alert" className="mb-3">
          Password aggiornata con successo.
        </Callout>
        <div className="text-center">
          <ButtonLink to="/login" variant="text" size="sm">
            Accedi con la nuova password
          </ButtonLink>
        </div>
      </>,
    )
  }

  return pageWrapper(
    <>
      <h1 className="md-typescale-title-medium mb-3">Nuova Password</h1>

      {error && (
        <Callout tone="error" role="alert" className="mb-3">
          {error}
        </Callout>
      )}

      <form onSubmit={(e) => { void handleSubmit(e) }}>
        <div className="mb-3">
          <TextField
            id="reset-password"
            label="Nuova Password"
            type="password"
            variant="outlined"
            autoComplete="new-password"
            autoFocus
            minLength={8}
            required
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <TextField
            id="reset-confirm"
            label="Conferma Password"
            type="password"
            variant="outlined"
            autoComplete="new-password"
            minLength={8}
            required
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
          />
        </div>

        <Button
          variant="filled"
          fullWidth
          type="submit"
          disabled={loading}
        >
          {loading ? 'Salvataggio…' : 'Salva nuova password'}
        </Button>
      </form>
    </>,
  )
}
